import type {
  ResourceNeeds,
  WorkAssignment,
} from 'components/IAP/hooks/workAssignmentHooks';

export type Ics215ResourceColumn = {
  id: string;
  label: string;
};

export function buildIcs215ResourceColumns(
  assignments: WorkAssignment[],
  extraColumnNames: string[] = []
): Ics215ResourceColumn[] {
  const names = new Set<string>();

  for (const assignment of assignments) {
    for (const resource of assignment.resource_list ?? []) {
      const name = (resource.resourceName ?? '').trim();
      if (name) names.add(name);
    }
  }

  for (const name of extraColumnNames) {
    const trimmed = name.trim();
    if (trimmed) names.add(trimmed);
  }

  return Array.from(names)
    .sort((a, b) => a.localeCompare(b))
    .map((label) => ({ id: label, label }));
}

export function getResourceValue(
  list: ResourceNeeds[] | undefined,
  resourceName: string
): { required: string; have: string; need: string } {
  const entry = list?.find((item) => item.resourceName === resourceName);
  return {
    required: entry?.required != null ? String(entry.required) : '',
    have: entry?.have != null ? String(entry.have) : '',
    need: entry?.need != null ? String(entry.need) : '',
  };
}

export function upsertResourceList(
  list: ResourceNeeds[] | undefined,
  resourceName: string,
  field: 'required' | 'have' | 'need',
  value: string
): ResourceNeeds[] {
  const next = [...(list ?? [])];
  const index = next.findIndex((item) => item.resourceName === resourceName);
  const parsed =
    value.trim() === '' ? ('' as unknown as number) : Number(value);
  const numeric = Number.isFinite(parsed as number) ? (parsed as number) : 0;

  if (index === -1) {
    if (value.trim() === '') return next;
    next.push({
      resourceName,
      required: field === 'required' ? numeric : 0,
      have: field === 'have' ? numeric : 0,
      need: field === 'need' ? numeric : 0,
    });
    return next;
  }

  const current = { ...next[index] };
  if (value.trim() === '') {
    current[field] = 0;
    const empty = !current.required && !current.have && !current.need;
    if (empty) {
      next.splice(index, 1);
      return next;
    }
  } else {
    current[field] = numeric;
  }
  next[index] = current;
  return next;
}

export function computeColumnTotals(
  assignments: WorkAssignment[],
  columns: Ics215ResourceColumn[]
): Record<string, { required: number; have: number; need: number }> {
  const totals: Record<
    string,
    { required: number; have: number; need: number }
  > = {};

  for (const column of columns) {
    totals[column.id] = { required: 0, have: 0, need: 0 };
  }

  for (const assignment of assignments) {
    for (const resource of assignment.resource_list ?? []) {
      const name = resource.resourceName;
      if (!totals[name]) continue;
      totals[name].required += Number(resource.required) || 0;
      totals[name].have += Number(resource.have) || 0;
      totals[name].need += Number(resource.need) || 0;
    }
  }

  return totals;
}
